import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/Api';
import { api_url_v1 } from '../utils/Config';

const initialState = {
  items: [],
  selectedAlumnus: null,
  meta: {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  },
  loading: false,
  error: null
};

export const fetchAlumni = createAsyncThunk(
  'alumni/fetchAlumni',
  async ({ page, limit, activeFilters, search }, { getState }) => {
    const { auth: { token } } = getState();
    const processedParams = {};
    for (const key in activeFilters) {
      processedParams[key] = Array.isArray(activeFilters[key]) 
        ? activeFilters[key].join(',') 
        : activeFilters[key];
    }
    const params = {
      page,
      limit,
      search,
      ...processedParams
    }
    const response = await api.get(`${api_url_v1}/alumni`, {
      params,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    
    return {
      data: response.data.data || [],
      meta: response.data.meta || {
        current_page: page,
        last_page: 1,
        per_page: limit,
        total: 0
      }
    };
  }
);

export const createAlumnus = createAsyncThunk(
    'alumni/createAlumnus',
    async (alumniData, { getState, rejectWithValue }) => {
      try {
        const { auth: { token } } = getState();
        const response = await api.post(`${api_url_v1}/alumni`, alumniData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        return response.data.data;
      } catch (error) {
        return rejectWithValue(error.response?.data || 'Alumni creation failed');
      }
    }
);

export const fetchAlumnusDetails = createAsyncThunk(
  'alumni/fetchAlumnusDetails',
  async (id, { getState }) => {
    const { auth: { token } } = getState();
    const response = await api.get(`${api_url_v1}/alumni/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    return response.data;
  }
);

export const updateAlumnus = createAsyncThunk(
  'alumni/updateAlumnus',
  async ({ id, data }, { getState, rejectWithValue }) => {

    try {
      const { auth: { token } } = getState();
      const response = await api.put(`${api_url_v1}/alumni/${id}`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data ;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Update failed');
    }
  }
);

export const deleteAlumnus = createAsyncThunk(
    'alumni/deleteAlumnus',
    async (id, { getState, rejectWithValue }) => {
      try {
        const { auth: { token } } = getState();
        await api.delete(`${api_url_v1}/alumni/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        return id;
      } catch (error) {
        return rejectWithValue(error.response?.data || 'Delete failed');
      }
    }
);

export const importAlumni = createAsyncThunk(
    'alumni/importAlumni',
    async (file, { getState, rejectWithValue }) => {
      try {
        const { auth: { token } } = getState();
        
        // Create FormData object
        const formData = new FormData();
        formData.append('file', file);
  
        const response = await api.post(`${api_url_v1}/alumni/import`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data', // Important for file upload
          }
        });
        
        return response.data;
      } catch (error) {
        return rejectWithValue(error.response?.data || 'Import failed');
      }
    }
);

const alumniSlice = createSlice({
  name: 'alumni',
  initialState,
  reducers: {
    setSelectedAlumnus: (state, action) => {
      state.selectedAlumnus = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Alumni Cases
      .addCase(fetchAlumni.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlumni.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchAlumni.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch Alumnus Details Cases
      .addCase(fetchAlumnusDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlumnusDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAlumnus = action.payload.data;
      })
      .addCase(fetchAlumnusDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Update Alumnus Cases
      .addCase(updateAlumnus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAlumnus.fulfilled, (state, action) => {
        state.loading = false;
        // Update the alumnus in the items array
        const index = state.items.findIndex(alumnus => alumnus.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        
        // Update selectedAlumnus if it's the same alumnus
        if (state.selectedAlumnus?.id === action.payload.id) {
          state.selectedAlumnus = action.payload;
        }
      })
      .addCase(updateAlumnus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Update failed';
      })
      
      // Create Alumnus Cases
      .addCase(createAlumnus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAlumnus.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new alumnus to the items array
        state.items.push(action.payload);
        // Update total count in meta
        state.meta.total = (state.meta.total || 0) + 1;
      })
      .addCase(createAlumnus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Alumni creation failed';
      })
      
      // Delete Alumnus Cases
      .addCase(deleteAlumnus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAlumnus.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the deleted alumnus from the items array
        state.items = state.items.filter(alumnus => alumnus.id !== action.payload);
        // Clear selectedAlumnus if it was the deleted alumnus
        if (state.selectedAlumnus?.id === action.payload) {
          state.selectedAlumnus = null;
        }
        // Update the total count in meta
        state.meta.total = Math.max(0, (state.meta.total || 1) - 1);
      })
      .addCase(deleteAlumnus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Delete failed';
      })
      
      // Import Alumni Cases
      .addCase(importAlumni.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importAlumni.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally handle import response
        // You might want to dispatch fetchAlumni here to refresh the list
      })
      .addCase(importAlumni.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Import failed';
      })
      
      // Fetch Alumnus Details Cases
    //   .addCase(fetchAlumnusDetails.pending, (state) => {
    //     state.loading = true;
    //     state.error = null;
    //   })
    //   .addCase(fetchAlumnusDetails.fulfilled, (state, action) => {
    //     state.loading = false;
    //     state.selectedAlumnus = action.payload.data;
    //   })
    //   .addCase(fetchAlumnusDetails.rejected, (state, action) => {
    //     state.loading = false;
    //     state.error = action.error.message;
    //   })
      
      // Update Alumnus Cases
    //   .addCase(updateAlumnus.pending, (state) => {
    //     state.loading = true;
    //     state.error = null;
    //   })
    //   .addCase(updateAlumnus.fulfilled, (state, action) => {
    //     state.loading = false;
        
    //     // Update the alumnus in the items array
    //     const index = state.items.findIndex(alumnus => alumnus.id === action.payload.id);
    //     if (index !== -1) {
    //       state.items[index] = action.payload.updatedAlumnus;
    //     }
        
    //     // Update selectedAlumnus if it's the same alumnus
    //     if (state.selectedAlumnus?.id === action.payload.id) {
    //       state.selectedAlumnus = action.payload.updatedAlumnus;
    //     }
    //   })
    //   .addCase(updateAlumnus.rejected, (state, action) => {
    //     state.loading = true;
    //     state.error = action.payload || 'Update failed';
    //   })
    //   .addCase(createAlumnus.pending, (state) => {
    //     state.loading = true;
    //     state.error = null;
    //   })
    //   .addCase(createAlumnus.fulfilled, (state, action) => {
    //     state.loading = false;
    //     // Add the new alumnus to the items array
    //     state.items.push(action.payload);
    //   })
    //   .addCase(createAlumnus.rejected, (state, action) => {
    //     state.loading = false;
    //     state.error = action.payload || 'Alumni creation failed';
    //   })
    //   .addCase(deleteAlumnus.pending, (state) => {
    //     state.loading = true;
    //     state.error = null;
    //   })
    //   .addCase(deleteAlumnus.fulfilled, (state, action) => {
    //     state.loading = false;
    //     // Remove the deleted alumnus from the items array
    //     state.items = state.items.filter(alumnus => alumnus.id !== action.payload);
    //     // Clear selectedAlumnus if it was the deleted alumnus
    //     if (state.selectedAlumnus?.id === action.payload) {
    //       state.selectedAlumnus = null;
    //     }
    //     // Update the total count in meta
    //     state.meta.total = Math.max(0, state.meta.total - 1);
    //   })
    //   .addCase(deleteAlumnus.rejected, (state, action) => {
    //     state.loading = false;
    //     state.error = action.payload || 'Delete failed';
    //   })
    //   .addCase(importAlumni.pending, (state) => {
    //     state.loading = true;
    //     state.error = null;
    //   })
    //   .addCase(importAlumni.fulfilled, (state, action) => {
    //     state.loading = false;
    //     // Optionally refresh the alumni list or handle the response
    //     // You might want to dispatch fetchAlumni here
    //   })
    //   .addCase(importAlumni.rejected, (state, action) => {
    //     state.loading = false;
    //     state.error = action.payload || 'Import failed';
    //   });
  }
});

export const { setSelectedAlumnus } = alumniSlice.actions;
export default alumniSlice.reducer;